import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { materialsAPI } from '../utils/api';

const MaterialForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    barcode: '',
    cost: 0,
    unit: '',
    salePrice: 0,
    category: '',
    isOnDemand: false
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Ref to prevent duplicate API calls for the same material ID
  const loadedIdRef = useRef(null);

  useEffect(() => {
    if (isEdit && loadedIdRef.current !== id) {
      loadedIdRef.current = id;
      loadMaterial();
    }
  }, [id, isEdit]);

  const loadMaterial = async () => {
    try {
      const res = await materialsAPI.getById(id);
      setFormData(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const generateBarcode = async () => {
    let barcode;
    let isUnique = false;
    let attempts = 0;
    const maxAttempts = 10;

    while (!isUnique && attempts < maxAttempts) {
      // Generate 13-digit barcode (EAN-13 format)
      barcode = '';
      for (let i = 0; i < 13; i++) {
        barcode += Math.floor(Math.random() * 10);
      }

      try {
        // Check if barcode already exists
        const res = await materialsAPI.search(barcode);
        if (res.data.data.length === 0) {
          isUnique = true;
        }
      } catch (err) {
        console.error('Error checking barcode:', err);
      }

      attempts++;
    }

    if (isUnique) {
      setFormData({ ...formData, barcode });
    } else {
      setError('ไม่สามารถสร้างบาร์โค้ดที่ไม่ซ้ำได้ กรุณาลองอีกครั้ง');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Check if barcode is duplicate before submitting
    if (formData.barcode && formData.barcode.trim() !== '') {
      try {
        const res = await materialsAPI.search(formData.barcode);
        // If editing, exclude current material from duplicate check
        const duplicates = res.data.data.filter(m => m.id !== parseInt(id));
        if (duplicates.length > 0) {
          setError('บาร์โค้ดนี้มีอยู่ในระบบแล้ว กรุณาใช้บาร์โค้ดอื่น');
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Error checking barcode:', err);
      }
    }

    // Check if material code is duplicate before submitting
    if (formData.code && formData.code.trim() !== '') {
      try {
        const res = await materialsAPI.search(formData.code.trim());
        const duplicates = res.data.data.filter(m => m.id !== parseInt(id));
        if (duplicates.length > 0) {
          setError('รหัสวัตถุดิบนี้ถูกใช้งานแล้ว กรุณาใช้รหัสอื่น');
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Error checking code:', err);
      }
    }

    try {
      if (isEdit) {
        await materialsAPI.update(id, formData);
        alert('อัพเดทวัตถุดิบสำเร็จ');
      } else {
        await materialsAPI.create(formData);
        alert('เพิ่มวัตถุดิบสำเร็จ');
      }
      navigate('/materials');
    } catch (err) {
      setError(err.response?.data?.message || 'เกิดข้อผิดพลาด');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="main-content">
      <Row className="mb-4">
        <Col>
          <h2>
            <i className={`bi bi-${isEdit ? 'pencil' : 'plus-circle'} me-2`}></i>
            {isEdit ? 'แก้ไขวัตถุดิบ' : 'เพิ่มวัตถุดิบใหม่'}
          </h2>
        </Col>
      </Row>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>รหัสวัตถุดิบ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    required
                    placeholder="เช่น MAT001"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ชื่อวัตถุดิบ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="เช่น กระเพรา"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>บาร์โค้ด</Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="barcode"
                      value={formData.barcode}
                      onChange={handleChange}
                      placeholder="บาร์โค้ดสินค้า"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={generateBarcode}
                      title="สร้างบาร์โค้ดอัตโนมัติ"
                    >
                      <i className="bi bi-magic"></i> สร้าง
                    </Button>
                  </InputGroup>
                  <Form.Text className="text-muted">
                    คลิก "สร้าง" เพื่อสร้างบาร์โค้ดอัตโนมัติ (13 หลัก)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>หมวดหมู่</Form.Label>
                  <Form.Control
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    placeholder="เช่น ผัก, เนื้อสัตว์"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ต้นทุน (บาท) <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="cost"
                    value={formData.cost}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>ราคาขาย (บาท)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    name="salePrice"
                    value={formData.salePrice}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>หน่วยนับ <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    name="unit"
                    value={formData.unit}
                    onChange={handleChange}
                    required
                    placeholder="เช่น กก., ลิตร, ชิ้น"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="isOnDemand"
                    name="isOnDemand"
                    label="สั่งตามต้องการ (On-Demand) — ไม่แสดงสต็อก, เบิกได้ทันทีโดยไม่ต้องบังคับเบิก"
                    checked={!!formData.isOnDemand}
                    onChange={handleCheckboxChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="mt-4">
              <Button type="submit" variant="primary" disabled={loading} className="me-2">
                {loading ? 'กำลังบันทึก...' : 'บันทึก'}
              </Button>
              <Button variant="secondary" onClick={() => navigate('/materials')}>
                ยกเลิก
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default MaterialForm;
